/** Presentation helpers. Pure functions, so they can be tested in node. */

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;',
  })[c] as string);
}

/** "18.65°S, 173.98°W". */
export function coordinates(latDeg: number, lonDeg: number): string {
  const lat = `${Math.abs(latDeg).toFixed(2)}°${latDeg < 0 ? 'S' : 'N'}`;
  const lon = `${Math.abs(lonDeg).toFixed(2)}°${lonDeg < 0 ? 'W' : 'E'}`;
  return `${lat}, ${lon}`;
}

/** "11 h 01 min"; "24 h" and "0 h" for polar day and night. */
export function hoursMinutes(hours: number): string {
  if (hours >= 24) return '24 h';
  if (hours <= 0) return '0 h';
  let h = Math.floor(hours);
  let m = Math.round((hours - h) * 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return `${h} h ${String(m).padStart(2, '0')} min`;
}

/** A count to the nearest whole, its thousands marked: 41,000. */
export function whole(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** A count and its noun, one or many by the count shown (review M116: "1 of them pups", "1 adults ... (1 females, 0 males)"). */
export function counted(value: number, one: string, many: string): string {
  return `${whole(value)} ${Math.round(value) === 1 ? one : many}`;
}

/** So many of a group, one or many by the count shown: "1 of them a pup", "12 of them pups" (review M116). */
export function ofThem(value: number, one: string, many: string): string {
  return `${whole(value)} of them ${Math.round(value) === 1 ? one : many}`;
}

/** `value` to `digits` places, with a minus sign (−, not a hyphen) and never "−0.0". */
export function fixed(value: number, digits: number): string {
  const text = value.toFixed(digits);
  if (!text.startsWith('-')) return text;
  return /[1-9]/.test(text) ? `\u2212${text.slice(1)}` : text.slice(1);
}

/** `value` to `digits` places with its sign, plus or minus (−, not a hyphen), and none on zero: "+0.4", "−0.3", "0.0". */
export function signed(value: number, digits: number): string {
  const text = fixed(value, digits);
  return /[1-9]/.test(text) && !text.startsWith('−') ? `+${text}` : text;
}

/**
 * Markup's shape: its tags and attributes, its text left out. Two markups of one shape differ only in their words, so
 * the second is set by changing the text in place (`setMarkup`), every element kept (review 29: Explore's pane rebuilt
 * each day a tagged animal's days ran on, its buttons replaced under a press, as M9's were).
 */
export function shapeOf(html: string): string {
  return html.replace(/>[^<]*</g, '><');
}

/** Sets an element's markup: its words alone where only they changed (`shapeOf`), else the whole of it. */
export function setMarkup(el: HTMLElement, html: string): void {
  const shape = shapeOf(html);
  if (el.dataset.shape === shape) {
    const t = el.ownerDocument.createElement('template');
    t.innerHTML = html;
    const texts = (root: Node): Text[] => {
      const out: Text[] = [];
      const walk = el.ownerDocument.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
      for (let n = walk.nextNode(); n; n = walk.nextNode()) out.push(n as Text);
      return out;
    };
    const [now, next] = [texts(el), texts(t.content)];
    if (now.length === next.length) {
      now.forEach((n, i) => { const v = (next[i] as Text).nodeValue; if (n.nodeValue !== v) n.nodeValue = v; });
      return;
    }
  }
  el.innerHTML = html;
  el.dataset.shape = shape;
}
